import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import PortalLayout from '../components/PortalLayout';
import { Badge } from '../components/ui/badge';
import { Flask, Users, CalendarBlank, Clock, ArrowRight, Warning } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  activo: 'bg-green-50 text-green-700 border-green-200',
  finalizado: 'bg-stone-100 text-stone-600 border-stone-200',
};

const productTypeColors = {
  cosmetico: 'bg-pink-50 text-pink-700 border-pink-200',
  cosmetico_plus: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
  sanitario: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  suplemento: 'bg-orange-50 text-orange-700 border-orange-200',
  medicamento: 'bg-purple-50 text-purple-700 border-purple-200',
};

export default function PortalDashboard() {
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const lb = lang === 'es' ? {
    welcome: 'Panel de Seguimiento',
    activeStudies: 'Estudios activos', completedStudies: 'Estudios finalizados',
    totalStudies: 'Total estudios', noStudies: 'No hay estudios asignados',
    volunteers: 'Voluntarios', included: 'Incluidos', completed: 'Completados',
    appts: 'Citas', done: 'Realizadas', progress: 'Progreso',
    viewDetail: 'Ver detalle', loading: 'Cargando...',
    activo: 'Activo', finalizado: 'Finalizado',
    recruited: 'Reclutados', t0: 'Visitas T0', intermediate: 'Visitas intermedias',
    final: 'Visitas finales', startWeek: 'Semana de inicio', conclusion: 'Progreso Estudio',
    cosmetico: 'Cosmetico', cosmetico_plus: 'Cosmetico Plus', sanitario: 'Sanitario', suplemento: 'Suplemento', medicamento: 'Medicamento',
  } : {
    welcome: 'Tracking Dashboard',
    activeStudies: 'Active studies', completedStudies: 'Completed studies',
    totalStudies: 'Total studies', noStudies: 'No studies assigned',
    volunteers: 'Volunteers', included: 'Included', completed: 'Completed',
    appts: 'Appointments', done: 'Done', progress: 'Progress',
    viewDetail: 'View detail', loading: 'Loading...',
    activo: 'Active', finalizado: 'Completed',
    recruited: 'Recruited', t0: 'Initial visits (T0)', intermediate: 'Intermediate visits',
    final: 'Final visits', startWeek: 'Start week', conclusion: 'Study Progress',
    cosmetico: 'Cosmetic', cosmetico_plus: 'Cosmetic Plus', sanitario: 'Medical Device', suplemento: 'Supplement', medicamento: 'Drug',
  };

  useEffect(() => {
    axios.get(`${API_URL}/api/portal/studies`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const studies = data?.studies || [];
  const config = data?.config || {};
  const activeStudies = studies.filter(s => s.status === 'activo');
  const completedStudies = studies.filter(s => s.status === 'finalizado');

  return (
    <PortalLayout clientName={data?.client_name}>
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-3">
            <div className="h-8 w-8 border-2 border-[#1f3e80] border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-stone-500">{lb.loading}</span>
          </div>
        </div>
      ) : (
        <div className="space-y-8" data-testid="portal-dashboard">
          {/* Welcome + config message */}
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-stone-900">{lb.welcome}</h1>
            {config.welcome_message && (
              <p className="text-sm text-stone-500 mt-1">{config.welcome_message}</p>
            )}
          </div>

          {/* Summary KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white border border-stone-200 rounded-lg p-5 text-center">
              <p className="text-3xl font-bold font-mono text-[#1f3e80]">{activeStudies.length}</p>
              <p className="text-xs text-stone-500 mt-1">{lb.activeStudies}</p>
            </div>
            <div className="bg-white border border-stone-200 rounded-lg p-5 text-center">
              <p className="text-3xl font-bold font-mono text-stone-600">{completedStudies.length}</p>
              <p className="text-xs text-stone-500 mt-1">{lb.completedStudies}</p>
            </div>
            <div className="bg-white border border-stone-200 rounded-lg p-5 text-center">
              <p className="text-3xl font-bold font-mono text-stone-800">{studies.length}</p>
              <p className="text-xs text-stone-500 mt-1">{lb.totalStudies}</p>
            </div>
          </div>

          {/* Studies list */}
          {studies.length === 0 ? (
            <div className="bg-white border border-stone-200 rounded-lg p-12 text-center">
              <Flask size={40} weight="duotone" className="text-stone-300 mx-auto mb-3" />
              <p className="text-sm text-stone-500">{lb.noStudies}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {[...activeStudies, ...completedStudies].map(s => (
                <div
                  key={s.study_id}
                  data-testid={`portal-study-card-${s.study_id}`}
                  onClick={() => navigate(`/portal/study/${s.study_id}`)}
                  className="bg-white border border-stone-200 rounded-lg p-5 hover:border-[#1f3e80]/30 hover:shadow-sm transition-all cursor-pointer group"
                >
                  {/* Header row */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-sm font-semibold text-stone-900 truncate">{s.title}</h3>
                        <Badge variant="outline" className={`text-[10px] ${statusColors[s.status] || ''}`}>
                          {lb[s.status] || s.status}
                        </Badge>
                        {s.product_type && (
                          <Badge variant="outline" className={`text-[10px] ${productTypeColors[s.product_type] || ''}`}>
                            {lb[s.product_type] || s.product_type}
                          </Badge>
                        )}
                        {s.new_adverse_events > 0 && (
                          <Badge
                            variant="outline"
                            className="text-[10px] bg-red-50 text-red-700 border-red-300 gap-1 animate-pulse"
                            data-testid={`portal-new-ae-badge-${s.study_id}`}
                          >
                            <Warning size={10} weight="fill" />
                            {s.new_adverse_events} {lang === 'es' ? (s.new_adverse_events > 1 ? 'nuevos EA' : 'nuevo EA') : (s.new_adverse_events > 1 ? 'new AEs' : 'new AE')}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-stone-500 mt-2">
                        <span className="font-mono">{s.code || s.study_id}</span>
                        {s.start_date && (
                          <span className="flex items-center gap-1">
                            <Clock size={12} />
                            {s.start_date}{s.end_date ? ` — ${s.end_date}` : ''}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 flex-shrink-0">
                      {s.volunteers && config.show_volunteer_counts && (
                        <div className="text-center hidden sm:block">
                          <p className="text-lg font-bold font-mono text-[#1f3e80]">{s.volunteers.included}<span className="text-xs text-stone-400 font-normal">/{s.volunteers.total}</span></p>
                          <p className="text-[10px] text-stone-400">{lb.recruited}</p>
                        </div>
                      )}
                      <div className="text-center hidden sm:block">
                        <p className="text-lg font-bold font-mono text-stone-700">{s.timeline_pct}%</p>
                        <p className="text-[10px] text-stone-400">{lb.conclusion}</p>
                      </div>
                      <ArrowRight size={16} className="text-stone-300 group-hover:text-[#1f3e80] transition-colors" />
                    </div>
                  </div>

                  {/* Conclusion bar */}
                  <div className="mt-3 h-1.5 bg-stone-100 rounded-full overflow-hidden">
                    <div className="h-full bg-[#1f3e80] rounded-full transition-all" style={{ width: `${s.timeline_pct}%` }} />
                  </div>

                  {/* Start week */}
                  {s.start_week && (
                    <p className="text-[11px] text-stone-500 mt-3 font-medium">{lb.startWeek}: <span className="text-[#1f3e80] font-mono">{s.start_week}</span></p>
                  )}

                  {/* 4 progress bars */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
                    <ProgressMiniBar label={lb.recruited} pct={s.recruited_pct} color="bg-[#1f3e80]" />
                    <ProgressMiniBar label={lb.t0} pct={s.t0_pct} color="bg-green-500" />
                    <ProgressMiniBar label={lb.intermediate} pct={s.intermediate_pct} color="bg-amber-500" />
                    <ProgressMiniBar label={lb.final} pct={s.final_pct} color="bg-emerald-600" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </PortalLayout>
  );
}


function ProgressMiniBar({ label, pct, color }) {
  const val = pct || 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] text-stone-500 truncate">{label}</span>
        <span className="text-[11px] font-mono font-semibold text-stone-700 ml-1">{val}%</span>
      </div>
      <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${val}%` }} />
      </div>
    </div>
  );
}
